import React, { useState } from 'react';
import {
  TrendingDown, TrendingUp, Users, Calendar, Target, Building2,
  Play, Loader2, AlertTriangle, CheckCircle2, XCircle, Info,
  DollarSign, Clock, BarChart3, RefreshCw, ChevronDown, ChevronUp,
  Zap, Ban, FastForward, Pause, Layers, Sparkles
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useCurrency } from '../utils/currencyUtils';

// =============================================================================
// SCENARIO INTELLIGENCE PANEL
// =============================================================================
// Executes the 8 Scenario Types:
// 1. Budget Reduction
// 2. Budget Increase
// 3. Resource Reduction
// 4. Resource Increase
// 5. Delivery Delay
// 6. Delivery Acceleration
// 7. Strategic Priority Shift
// 8. Programme Termination
// =============================================================================

const SCENARIO_TYPES = [
  {
    id: 'budget-reduction',
    name: 'Budget Reduction',
    icon: TrendingDown,
    color: 'red',
    description: 'Model the impact of reducing portfolio budget',
    inputType: 'percentage',
    inputLabel: 'Reduction %',
    inputMin: 5,
    inputMax: 50,
    inputDefault: 10,
    endpoint: '/scenario-intelligence/scenarios/budget-reduction',
    method: 'POST',
    buildPayload: (value) => ({ reduction_percentage: value })
  },
  {
    id: 'budget-increase',
    name: 'Budget Increase',
    icon: TrendingUp,
    color: 'emerald',
    description: 'Model the impact of increasing portfolio budget',
    inputType: 'percentage',
    inputLabel: 'Increase %',
    inputMin: 5,
    inputMax: 50,
    inputDefault: 10,
    endpoint: '/scenario-intelligence/scenarios/budget-increase',
    method: 'POST',
    buildPayload: (value) => ({ increase_percentage: value })
  },
  {
    id: 'resource-reduction',
    name: 'Resource Reduction',
    icon: Users,
    color: 'orange',
    description: 'Model the impact of reducing FTE capacity',
    inputType: 'percentage',
    inputLabel: 'FTE Reduction %',
    inputMin: 5,
    inputMax: 40,
    inputDefault: 10,
    endpoint: '/scenario-intelligence/scenarios/resource-reduction',
    method: 'POST',
    buildPayload: (value) => ({ reduction_percentage: value })
  },
  {
    id: 'resource-increase',
    name: 'Resource Increase',
    icon: Users,
    color: 'blue',
    description: 'Model the impact of adding FTE capacity',
    inputType: 'percentage',
    inputLabel: 'FTE Increase %',
    inputMin: 5,
    inputMax: 40,
    inputDefault: 10,
    endpoint: '/scenario-intelligence/scenarios/resource-increase',
    method: 'POST',
    buildPayload: (value) => ({ increase_percentage: value })
  },
  {
    id: 'delivery-delay',
    name: 'Delivery Delay',
    icon: Pause,
    color: 'amber',
    description: 'Model the cascade effect of timeline delays',
    inputType: 'weeks',
    inputLabel: 'Delay (weeks)',
    inputMin: 2,
    inputMax: 26,
    inputDefault: 4,
    endpoint: '/scenario-intelligence/scenarios/delivery-delay',
    method: 'POST',
    buildPayload: (value) => ({ delay_weeks: value })
  },
  {
    id: 'delivery-acceleration',
    name: 'Delivery Acceleration',
    icon: FastForward,
    color: 'cyan',
    description: 'Model the impact of accelerating delivery',
    inputType: 'weeks',
    inputLabel: 'Acceleration (weeks)',
    inputMin: 2,
    inputMax: 12,
    inputDefault: 4,
    endpoint: '/scenario-intelligence/scenarios/delivery-acceleration',
    method: 'POST',
    buildPayload: (value) => ({ acceleration_weeks: value })
  },
  {
    id: 'strategic-priority-shift',
    name: 'Priority Shift',
    icon: Target,
    color: 'violet',
    description: 'Model reallocating resources to strategic priorities',
    inputType: 'percentage',
    inputLabel: 'Reallocation %',
    inputMin: 10,
    inputMax: 50,
    inputDefault: 20,
    endpoint: '/scenario-intelligence/scenarios/strategic-priority-shift',
    method: 'POST',
    buildPayload: (value) => ({ reallocation_percentage: value })
  },
  {
    id: 'programme-termination',
    name: 'Programme Termination',
    icon: Ban,
    color: 'rose',
    description: 'Model the impact of terminating programmes',
    inputType: 'count',
    inputLabel: 'Programmes to stop',
    inputMin: 1,
    inputMax: 5,
    inputDefault: 1,
    endpoint: '/scenario-intelligence/scenarios/programme-termination',
    method: 'POST',
    buildPayload: (value) => ({ termination_count: value })
  }
];

const colorClasses = {
  red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-500', text: 'text-red-600' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-500', text: 'text-emerald-600' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-500', text: 'text-orange-600' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-500', text: 'text-blue-600' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-500', text: 'text-amber-600' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'bg-cyan-500', text: 'text-cyan-600' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', icon: 'bg-violet-500', text: 'text-violet-600' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'bg-rose-500', text: 'text-rose-600' }
};

const ScenarioResultPanel = ({ result, formatCurrency }) => {
  if (!result) return null;

  const getRecommendationColor = (rec) => {
    if (rec?.includes('PROCEED') && rec?.includes('CAUTION')) return 'text-amber-600 bg-amber-50';
    if (rec?.includes('PROCEED')) return 'text-emerald-600 bg-emerald-50';
    if (rec?.includes('NOT RECOMMENDED') || rec?.includes('AVOID')) return 'text-red-600 bg-red-50';
    return 'text-blue-600 bg-blue-50';
  };

  const impacts = result.impacts || {};

  return (
    <div className="mt-4 space-y-4 border-t pt-4" data-testid="scenario-result">
      {/* Recommendation Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium ${getRecommendationColor(result.recommendation)}`}>
        {result.recommendation?.includes('PROCEED') && !result.recommendation?.includes('NOT') ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <AlertTriangle className="w-5 h-5" />
        )}
        {result.recommendation}
      </div>

      {/* Confidence */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Confidence:</span>
        <span className={`font-medium ${
          result.confidence === 'HIGH' ? 'text-emerald-600' :
          result.confidence === 'MEDIUM' ? 'text-amber-600' : 'text-red-600'
        }`}>
          {result.confidence}
        </span>
      </div>

      {/* Rationale */}
      {result.rationale && (
        <p className="text-sm text-slate-600 italic bg-slate-50 p-3 rounded-lg">
          &ldquo;{result.rationale}&rdquo;
        </p>
      )}

      {/* Impact Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Investment Impact */}
        {impacts.investment && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <DollarSign className="w-3 h-3" />
              Investment Impact
            </div>
            <p className="text-lg font-bold text-slate-900">
              {formatCurrency(impacts.investment.reduction_amount || impacts.investment.increase_amount || 0)}
            </p>
            <p className="text-xs text-slate-500">
              {impacts.investment.reduction_percentage || impacts.investment.increase_percentage || 0}% of budget
            </p>
          </div>
        )}

        {/* Resource Impact */}
        {impacts.resource && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Users className="w-3 h-3" />
              Resource Impact
            </div>
            <p className="text-lg font-bold text-slate-900">
              {(impacts.resource.fte_impact || 0).toFixed(0)} FTE
            </p>
            <p className="text-xs text-slate-500">
              {(impacts.resource.fte_percentage || 0).toFixed(1)}% of capacity
            </p>
          </div>
        )}

        {/* Timeline Impact */}
        {impacts.timeline && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Clock className="w-3 h-3" />
              Timeline Impact
            </div>
            <p className="text-lg font-bold text-slate-900">
              {(impacts.timeline.avg_delay_weeks || 0).toFixed(1)} weeks
            </p>
            <p className="text-xs text-slate-500">
              {((impacts.timeline.extension_factor || 1) * 100 - 100).toFixed(0)}% extension
            </p>
          </div>
        )}

        {/* Benefit Impact */}
        {impacts.benefit && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Target className="w-3 h-3" />
              Benefit Impact
            </div>
            <p className="text-lg font-bold text-emerald-600">
              {(impacts.benefit.preservation_rate || 0).toFixed(0)}%
            </p>
            <p className="text-xs text-slate-500">
              benefit preserved
            </p>
          </div>
        )}

        {/* Projects Affected */}
        {impacts.project && (
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Layers className="w-3 h-3" />
              Projects Affected
            </div>
            <p className="text-lg font-bold text-slate-900">
              {impacts.project.projects_affected || 0}
            </p>
            <p className="text-xs text-slate-500">
              of {impacts.project.total_projects || 0} total
            </p>
          </div>
        )}
      </div>

      {/* Scoring */}
      {result.scoring && (
        <div className="mt-3">
          <p className="text-xs text-slate-400 mb-2">Composite Score</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  result.scoring.composite_score >= 70 ? 'bg-emerald-500' :
                  result.scoring.composite_score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${result.scoring.composite_score || 0}%` }}
              />
            </div>
            <span className="text-sm font-bold text-slate-700">
              {(result.scoring.composite_score || 0).toFixed(1)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const ScenarioCard = ({ scenario, onRun, loading, result, formatCurrency }) => {
  const [inputValue, setInputValue] = useState(scenario.inputDefault);
  const [expanded, setExpanded] = useState(false);
  const colors = colorClasses[scenario.color];
  const Icon = scenario.icon;

  return (
    <div 
      className={`rounded-xl border ${colors.bg} ${colors.border} overflow-hidden`}
      data-testid={`scenario-${scenario.id}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors.icon}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900">{scenario.name}</h3>
            <p className="text-sm text-slate-500">{scenario.description}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Input Controls */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm text-slate-500 mb-1">{scenario.inputLabel}</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={scenario.inputMin}
                  max={scenario.inputMax}
                  value={inputValue}
                  onChange={(e) => setInputValue(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                  data-testid={`scenario-${scenario.id}-input`}
                />
                <span className={`text-lg font-bold ${colors.text} min-w-[60px] text-right`}>
                  {inputValue}{scenario.inputType === 'percentage' ? '%' : scenario.inputType === 'weeks' ? 'w' : ''}
                </span>
              </div>
            </div>
            <button
              onClick={() => onRun(scenario, inputValue)}
              disabled={loading}
              className={`px-4 py-2 ${colors.icon} text-white rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50`}
              data-testid={`scenario-${scenario.id}-run`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run
            </button>
          </div>

          {/* Results */}
          {result && <ScenarioResultPanel result={result} formatCurrency={formatCurrency} />}
        </div>
      )}
    </div>
  );
};

const ScenarioIntelligencePanel = () => {
  const { tenant, activeTenantId } = useTenant();
  const { formatCurrency } = useCurrency();
  const tenantId = activeTenantId || tenant?.id;

  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});

  const runScenario = async (scenario, inputValue) => {
    if (!tenantId) return;

    setLoading(prev => ({ ...prev, [scenario.id]: true }));
    setErrors(prev => ({ ...prev, [scenario.id]: null }));

    try {
      const payload = scenario.buildPayload(inputValue);
      const response = await api.post(scenario.endpoint, payload, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      setResults(prev => ({ ...prev, [scenario.id]: response.data }));
    } catch (err) {
      console.error(`Scenario ${scenario.id} failed:`, err);
      setErrors(prev => ({ ...prev, [scenario.id]: err.message || 'Scenario failed' }));
    } finally {
      setLoading(prev => ({ ...prev, [scenario.id]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="scenario-intelligence-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl">
          <BarChart3 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Scenario Intelligence</h2>
          <p className="text-sm text-slate-500">Run what-if scenarios on your portfolio</p>
        </div>
      </div>

      {/* Scenario Grid */}
      <div className="grid gap-3 md:grid-cols-2">
        {SCENARIO_TYPES.map(scenario => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            onRun={runScenario}
            loading={loading[scenario.id]}
            result={results[scenario.id]}
            formatCurrency={formatCurrency}
          />
        ))}
      </div>

      {/* Error Display */}
      {Object.entries(errors).filter(([_, err]) => err).map(([id, err]) => (
        <div key={id} className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{err}</span>
        </div>
      ))}
    </div>
  );
};

export default ScenarioIntelligencePanel;
